import { Component, OnInit } from '@angular/core';
import { Config } from '../../core/DefaultConfig';
import { MatCard, MatCardContent } from '@angular/material/card';
import { TranslateModule } from '@ngx-translate/core';
import { MatError, MatFormField, MatLabel } from "@angular/material/form-field";
import { MatOption } from "@angular/material/core";
import { MatSelect } from "@angular/material/select";
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ApplicationDataService } from "../../core/services/application-data.service";
import { AppUrlConstants } from '../../core/constants/AppUrlConstants';
import { AlertDialogComponent } from '../../shared/alert-dialog/alert-dialog.component';
import { AuthenticationService } from '../../core/services/authentication.service';
import { MatDialog } from '@angular/material/dialog';
import { GenericCrudService } from '../../core/services/generic-crud.service';
import { AdminStructure } from '../../core/constants/adminStructure.const';
import { forkJoin } from 'rxjs';

// Interfaces
interface AdminStructureItem {
  adminStrId: number;
  name: string;
  parentAdminStrId?: number;
}

interface WorkflowStep {
  complaintProcessId: number;
  name: string;
}

interface User {
  userId: number;
  userName: string;
}

type AdminStructureType = 'DIVISION' | 'DISTRICT' | 'TEHSIL' | 'MOUZA';

@Component({
  selector: 'app-user-rights',
  standalone: true,
  imports: [
    MatCard, MatCardContent, MatError, MatFormField,
    MatLabel, MatOption, MatSelect, ReactiveFormsModule,
    TranslateModule, FormsModule
  ],
  templateUrl: './user-rights.component.html',
  styleUrls: ['./user-rights.component.css']
})
export class UserRightsComponent implements OnInit {
  config = new Config();
  form: FormGroup;

  readonly PROCESS: string = 'PROCESS';
  readonly adminStructure: any = AdminStructure;

  // User state
  usersList: User[] = [];
  filteredusersList: User[] = [];
  userSearchTerm: string = '';
  showUserDropdown: boolean = false;
  selectedUserId: number | null = null;
  selectedUserName: string = '';

  // ALL source data (pre-loaded)
  allDivisions: AdminStructureItem[] = [];
  allDistricts: AdminStructureItem[] = [];
  allTehsils: AdminStructureItem[] = [];
  allMouzas: AdminStructureItem[] = [];
  allWorkflowSteps: WorkflowStep[] = [];

  // Original lists (kept for backward compatibility)
  divisionList: any[] = [];
  districtList: any[] = [];
  tehsilList: any[] = [];
  mouzaList: any[] = [];
  workFlowStepsList: any[] = [];

  // Assigned items (right panel)
  selectedDivisionList: any[] = [];
  selectedDistrictList: any[] = [];
  selectedTehsilList: any[] = [];
  selectedMouzaList: any[] = [];
  selectedWorkFlowStepsList: any[] = [];

  // Display lists (available items - left panel)
  displayDivisionList: any[] = [];
  displayDistrictList: any[] = [];
  displayTehsilList: any[] = [];
  displayMouzaList: any[] = [];
  displayWorkFlowStepsList: any[] = [];

  // Search terms
  availableSearchProcess = '';
  assignedSearchProcess = '';
  availableSearchDivision = '';
  assignedSearchDivision = '';
  availableSearchDistrict = '';
  assignedSearchDistrict = '';
  availableSearchTehsil = '';
  assignedSearchTehsil = '';
  availableSearchMouza = '';
  assignedSearchMouza = '';

  // Loading states
  isInitialLoading: boolean = true;
  isLoadingUser: boolean = false;
  isSaving: boolean = false;
  hasUnsavedChanges = false;

  // Checkbox state
  checkedAvailable: { [type: string]: any[] } = {};
  checkedAssigned: { [type: string]: any[] } = {};

  constructor(
    private formBuilder: FormBuilder,
    private appDataService: ApplicationDataService,
    private authService: AuthenticationService,
    private crudService: GenericCrudService,
    public dialog: MatDialog
  ) { }

  ngOnInit() {
    this.initForm();
    this.loadAllData();
  }

  private initForm(): void {
    this.form = this.formBuilder.group({
      userId: [null],
      workFlowSteps: [null],
      divisions: [null],
      districts: [null],
      tehsil: [null],
      mouza: [null],
      divisionsIds: [[]],
      districtsIds: [[]],
      tehsilIds: [[]],
      mouzaIds: [[]],
      workFlowStepsIds: [[]]
    });

    // User selection change handler
    this.f['userId'].valueChanges.subscribe((value) => {
      if (value) {
        this.loadUserPermissions(value);
      }
    });
  }

  get f(): { [key: string]: AbstractControl } {
    return this.form.controls;
  }

  // ==================== DATA LOADING ====================

  private loadAllData(): void {
    this.isInitialLoading = true;
    const segmentId = this.authService.getUserSegment() || 1;

    forkJoin({
      users: this.appDataService.getAllInternalUsers(),
      workflowSteps: this.appDataService.getAllWorkFlowStepsBySegment(segmentId),
      divisions: this.appDataService.getByType(AdminStructure.DIVISION)
    }).subscribe({
      next: (result: any) => {
        this.usersList = result.users?.data || [];
        this.filteredusersList = result.users?.data || [];
        this.workFlowStepsList = result.workflowSteps?.data || [];
        this.divisionList = result.divisions?.data || [];

        this.display(this.PROCESS);
        this.display(this.adminStructure.DIVISION);

        if (this.divisionList.length > 0) {
          const divisionIds = this.divisionList.map((d: any) => d.adminStrId);
          this.loadAllChildItems(divisionIds, 'districts');
        } else {
          this.isInitialLoading = false;
        }
      },
      error: (err) => {
        console.error('Error loading initial data', err);
        this.openAlert('Failed to load data');
        this.isInitialLoading = false;
      }
    });
  }

  private loadAllChildItems(parentAdminStrIds: number[], level: 'districts' | 'tehsils' | 'mouzas'): void {
    this.appDataService.getAdminStructureByParentIds(parentAdminStrIds).subscribe({
      next: (result: any) => {
        const items = result?.data || [];

        if (level === 'districts') {
          this.districtList = items;
          this.display(this.adminStructure.DISTRICT);
          if (items.length > 0) {
            const districtIds = items.map((d: any) => d.adminStrId);
            this.loadAllChildItems(districtIds, 'tehsils');
          } else {
            this.isInitialLoading = false;
          }
        } else if (level === 'tehsils') {
          this.tehsilList = items;
          this.display(this.adminStructure.TEHSIL);
          if (items.length > 0) {
            const tehsilIds = items.map((t: any) => t.adminStrId);
            this.loadAllChildItems(tehsilIds, 'mouzas');
          } else {
            this.isInitialLoading = false;
          }
        } else if (level === 'mouzas') {
          this.mouzaList = items;
          this.display(this.adminStructure.MOUZA);
          this.isInitialLoading = false;
        }
      },
      error: (err) => {
        console.error(`Error loading ${level}`, err);
        this.isInitialLoading = false;
      }
    });
  }

  // ==================== USER SELECTION ====================

  onUserSearchChange(event: any): void {
    const value = event?.target?.value || '';
    this.userSearchTerm = value;

    if (!value) {
      this.filteredusersList = this.usersList;
    } else {
      const term = value.toLowerCase();
      this.filteredusersList = this.usersList.filter(u =>
        u.userName?.toLowerCase().includes(term)
      );
    }
  }

  onUserSelect(user: User): void {
    this.showUserDropdown = false;
    this.userSearchTerm = user.userName;
    this.selectedUserName = user.userName;
    this.f['userId'].setValue(user.userId);
  }

  private loadUserPermissions(userId: number): void {
    this.selectedUserId = userId;
    this.isLoadingUser = true;
    this.hasUnsavedChanges = false;

    this.appDataService.getPermissionByUser(userId).subscribe({
      next: (res: any) => {
        this.isLoadingUser = false;
        if (res?.data) {
          this.selectedDivisionList = res.data.divisions || [];
          this.selectedDistrictList = res.data.districts || [];
          this.selectedTehsilList = res.data.tehsil || [];
          this.selectedMouzaList = res.data.mouza || [];
          this.selectedWorkFlowStepsList = res.data.workFlowSteps || [];
        } else {
          this.clearAllAssigned();
        }
        this.updateFormIds();
        this.displayAll();
        this.clearAllChecked();
      },
      error: (err) => {
        this.isLoadingUser = false;
        console.error('Error loading permissions', err);
        this.openAlert('Failed to load user permissions');
        this.clearAllAssigned();
      }
    });
  }

  private clearAllAssigned(): void {
    this.selectedDivisionList = [];
    this.selectedDistrictList = [];
    this.selectedTehsilList = [];
    this.selectedMouzaList = [];
    this.selectedWorkFlowStepsList = [];
    this.updateFormIds();
  }

  private updateFormIds(): void {
    this.f['workFlowStepsIds'].setValue(this.getIds(this.selectedWorkFlowStepsList, this.PROCESS));
    this.f['divisionsIds'].setValue(this.getIds(this.selectedDivisionList, this.adminStructure.DIVISION));
    this.f['districtsIds'].setValue(this.getIds(this.selectedDistrictList, this.adminStructure.DISTRICT));
    this.f['tehsilIds'].setValue(this.getIds(this.selectedTehsilList, this.adminStructure.TEHSIL));
    this.f['mouzaIds'].setValue(this.getIds(this.selectedMouzaList, this.adminStructure.MOUZA));
  }

  private display(type: string): void {
    let newElements: any[] = [];
    if (type === this.PROCESS) {
      const selectedIds = this.f['workFlowStepsIds']?.value || [];
      (this.workFlowStepsList || []).forEach((u: any) => {
        if (!selectedIds.includes(u.complaintProcessId)) newElements.push(u);
      });
      this.displayWorkFlowStepsList = newElements;
    } else if (type === this.adminStructure.DIVISION) {
      const selectedIds = this.f['divisionsIds']?.value || [];
      (this.divisionList || []).forEach((u: any) => {
        if (!selectedIds.includes(u.adminStrId)) newElements.push(u);
      });
      this.displayDivisionList = newElements;
    } else if (type === this.adminStructure.DISTRICT) {
      const assignedDistrictIds = this.f['districtsIds']?.value || [];
      const assignedDivisionIds = this.f['divisionsIds']?.value || [];
      (this.districtList || []).forEach((u: any) => {
        const parentAssigned = assignedDivisionIds.includes(u.parentAdminStrId);
        const notYetAssigned = !assignedDistrictIds.includes(u.adminStrId);
        if (parentAssigned && notYetAssigned) newElements.push(u);
      });
      this.displayDistrictList = newElements;
    } else if (type === this.adminStructure.TEHSIL) {
      const assignedTehsilIds = this.f['tehsilIds']?.value || [];
      const assignedDistrictIds = this.f['districtsIds']?.value || [];
      (this.tehsilList || []).forEach((u: any) => {
        const parentAssigned = assignedDistrictIds.includes(u.parentAdminStrId);
        const notYetAssigned = !assignedTehsilIds.includes(u.adminStrId);
        if (parentAssigned && notYetAssigned) newElements.push(u);
      });
      this.displayTehsilList = newElements;
    } else if (type === this.adminStructure.MOUZA) {
      const assignedMouzaIds = this.f['mouzaIds']?.value || [];
      const assignedTehsilIds = this.f['tehsilIds']?.value || [];
      (this.mouzaList || []).forEach((u: any) => {
        const parentAssigned = assignedTehsilIds.includes(u.parentAdminStrId);
        const notYetAssigned = !assignedMouzaIds.includes(u.adminStrId);
        if (parentAssigned && notYetAssigned) newElements.push(u);
      });
      this.displayMouzaList = newElements;
    }
  }

  private displayAll(): void {
    this.display(this.PROCESS);
    this.display(this.adminStructure.DIVISION);
    this.display(this.adminStructure.DISTRICT);
    this.display(this.adminStructure.TEHSIL);
    this.display(this.adminStructure.MOUZA);
  }

  // ==================== CASCADE REMOVAL ====================

  private cascadeRemoveDivision(divisionId: number): void {
    const childDistrictIds = this.selectedDistrictList
      .filter(d => d.parentAdminStrId === divisionId)
      .map(d => d.adminStrId);
    this.selectedDistrictList = this.selectedDistrictList.filter(d => d.parentAdminStrId !== divisionId);
    this.f['districtsIds'].setValue(this.getIds(this.selectedDistrictList, this.adminStructure.DISTRICT));
    childDistrictIds.forEach(id => this.cascadeRemoveDistrict(id));
    this.display(this.adminStructure.DISTRICT);
  }

  private cascadeRemoveDistrict(districtId: number): void {
    const childTehsilIds = this.selectedTehsilList
      .filter(t => t.parentAdminStrId === districtId)
      .map(t => t.adminStrId);
    this.selectedTehsilList = this.selectedTehsilList.filter(t => t.parentAdminStrId !== districtId);
    this.f['tehsilIds'].setValue(this.getIds(this.selectedTehsilList, this.adminStructure.TEHSIL));
    childTehsilIds.forEach(id => this.cascadeRemoveTehsil(id));
    this.display(this.adminStructure.TEHSIL);
  }

  private cascadeRemoveTehsil(tehsilId: number): void {
    this.selectedMouzaList = this.selectedMouzaList.filter(m => m.parentAdminStrId !== tehsilId);
    this.f['mouzaIds'].setValue(this.getIds(this.selectedMouzaList, this.adminStructure.MOUZA));
    this.display(this.adminStructure.MOUZA);
  }

  // ==================== CHECKBOX OPERATIONS ====================

  getItemId(item: any, type: string): any {
    return type === this.PROCESS ? item.complaintProcessId : item.adminStrId;
  }

  isCheckedAvailable(item: any, type: string): boolean {
    const id = this.getItemId(item, type);
    return (this.checkedAvailable[type] || []).includes(id);
  }

  isCheckedAssigned(item: any, type: string): boolean {
    const id = this.getItemId(item, type);
    return (this.checkedAssigned[type] || []).includes(id);
  }

  toggleAvailableCheck(item: any, type: string): void {
    const id = this.getItemId(item, type);
    if (!this.checkedAvailable[type]) this.checkedAvailable[type] = [];
    const idx = this.checkedAvailable[type].indexOf(id);
    if (idx >= 0) this.checkedAvailable[type].splice(idx, 1);
    else this.checkedAvailable[type].push(id);
  }

  toggleAssignedCheck(item: any, type: string): void {
    const id = this.getItemId(item, type);
    if (!this.checkedAssigned[type]) this.checkedAssigned[type] = [];
    const idx = this.checkedAssigned[type].indexOf(id);
    if (idx >= 0) this.checkedAssigned[type].splice(idx, 1);
    else this.checkedAssigned[type].push(id);
  }

  clearAllChecked(): void {
    this.checkedAvailable = {};
    this.checkedAssigned = {};
  }

  // ==================== MOVE OPERATIONS ====================

  moveToAssigned(type: string): void {
    const checkedIds = this.checkedAvailable[type] || [];
    if (checkedIds.length === 0) return;

    if (type === this.PROCESS) {
      const items = this.displayWorkFlowStepsList.filter(i => checkedIds.includes(i.complaintProcessId));
      this.selectedWorkFlowStepsList.push(...items);
      this.f['workFlowStepsIds'].setValue(this.getIds(this.selectedWorkFlowStepsList, type));
    } else if (type === this.adminStructure.DIVISION) {
      const items = this.displayDivisionList.filter(i => checkedIds.includes(i.adminStrId));
      this.selectedDivisionList.push(...items);
      this.f['divisionsIds'].setValue(this.getIds(this.selectedDivisionList, type));
      this.display(this.adminStructure.DISTRICT);
    } else if (type === this.adminStructure.DISTRICT) {
      const items = this.displayDistrictList.filter(i => checkedIds.includes(i.adminStrId));
      this.selectedDistrictList.push(...items);
      this.f['districtsIds'].setValue(this.getIds(this.selectedDistrictList, type));
      this.display(this.adminStructure.TEHSIL);
    } else if (type === this.adminStructure.TEHSIL) {
      const items = this.displayTehsilList.filter(i => checkedIds.includes(i.adminStrId));
      this.selectedTehsilList.push(...items);
      this.f['tehsilIds'].setValue(this.getIds(this.selectedTehsilList, type));
      this.display(this.adminStructure.MOUZA);
    } else if (type === this.adminStructure.MOUZA) {
      const items = this.displayMouzaList.filter(i => checkedIds.includes(i.adminStrId));
      this.selectedMouzaList.push(...items);
      this.f['mouzaIds'].setValue(this.getIds(this.selectedMouzaList, type));
    }

    this.checkedAvailable[type] = [];
    this.display(type);
    this.hasUnsavedChanges = true;
  }

  moveToAvailable(type: string): void {
    const checkedIds = this.checkedAssigned[type] || [];
    if (checkedIds.length === 0) return;

    if (type === this.PROCESS) {
      this.selectedWorkFlowStepsList = this.selectedWorkFlowStepsList.filter(i => !checkedIds.includes(i.complaintProcessId));
      this.f['workFlowStepsIds'].setValue(this.getIds(this.selectedWorkFlowStepsList, type));
    } else if (type === this.adminStructure.DIVISION) {
      this.selectedDivisionList = this.selectedDivisionList.filter(i => !checkedIds.includes(i.adminStrId));
      this.f['divisionsIds'].setValue(this.getIds(this.selectedDivisionList, type));
      checkedIds.forEach((id: number) => this.cascadeRemoveDivision(id));
    } else if (type === this.adminStructure.DISTRICT) {
      this.selectedDistrictList = this.selectedDistrictList.filter(i => !checkedIds.includes(i.adminStrId));
      this.f['districtsIds'].setValue(this.getIds(this.selectedDistrictList, type));
      checkedIds.forEach((id: number) => this.cascadeRemoveDistrict(id));
    } else if (type === this.adminStructure.TEHSIL) {
      this.selectedTehsilList = this.selectedTehsilList.filter(i => !checkedIds.includes(i.adminStrId));
      this.f['tehsilIds'].setValue(this.getIds(this.selectedTehsilList, type));
      checkedIds.forEach((id: number) => this.cascadeRemoveTehsil(id));
    } else if (type === this.adminStructure.MOUZA) {
      this.selectedMouzaList = this.selectedMouzaList.filter(i => !checkedIds.includes(i.adminStrId));
      this.f['mouzaIds'].setValue(this.getIds(this.selectedMouzaList, type));
    }

    this.checkedAssigned[type] = [];
    this.display(type);
    this.hasUnsavedChanges = true;
  }

  selectAll(type: string): void {
    if (type === this.PROCESS) {
      this.selectedWorkFlowStepsList = [...this.workFlowStepsList];
      this.f['workFlowStepsIds'].setValue(this.getIds(this.selectedWorkFlowStepsList, type));
    } else if (type === this.adminStructure.DIVISION) {
      this.selectedDivisionList = [...this.divisionList];
      this.f['divisionsIds'].setValue(this.getIds(this.selectedDivisionList, type));
      this.display(this.adminStructure.DISTRICT);
    } else if (type === this.adminStructure.DISTRICT) {
      this.selectedDistrictList.push(...this.displayDistrictList);
      this.f['districtsIds'].setValue(this.getIds(this.selectedDistrictList, type));
      this.display(this.adminStructure.TEHSIL);
    } else if (type === this.adminStructure.TEHSIL) {
      this.selectedTehsilList.push(...this.displayTehsilList);
      this.f['tehsilIds'].setValue(this.getIds(this.selectedTehsilList, type));
      this.display(this.adminStructure.MOUZA);
    } else if (type === this.adminStructure.MOUZA) {
      this.selectedMouzaList.push(...this.displayMouzaList);
      this.f['mouzaIds'].setValue(this.getIds(this.selectedMouzaList, type));
    }
    this.checkedAvailable[type] = [];
    this.display(type);
    this.hasUnsavedChanges = true;
  }

  removeAll(type: string): void {
    if (type === this.PROCESS) {
      this.selectedWorkFlowStepsList = [];
      this.f['workFlowStepsIds'].setValue([]);
    } else if (type === this.adminStructure.DIVISION) {
      this.selectedDivisionList = [];
      this.selectedDistrictList = [];
      this.selectedTehsilList = [];
      this.selectedMouzaList = [];
      this.f['divisionsIds'].setValue([]);
      this.f['districtsIds'].setValue([]);
      this.f['tehsilIds'].setValue([]);
      this.f['mouzaIds'].setValue([]);
      this.display(this.adminStructure.DISTRICT);
      this.display(this.adminStructure.TEHSIL);
      this.display(this.adminStructure.MOUZA);
    } else if (type === this.adminStructure.DISTRICT) {
      this.selectedDistrictList = [];
      this.selectedTehsilList = [];
      this.selectedMouzaList = [];
      this.f['districtsIds'].setValue([]);
      this.f['tehsilIds'].setValue([]);
      this.f['mouzaIds'].setValue([]);
      this.display(this.adminStructure.TEHSIL);
      this.display(this.adminStructure.MOUZA);
    } else if (type === this.adminStructure.TEHSIL) {
      this.selectedTehsilList = [];
      this.selectedMouzaList = [];
      this.f['tehsilIds'].setValue([]);
      this.f['mouzaIds'].setValue([]);
      this.display(this.adminStructure.MOUZA);
    } else if (type === this.adminStructure.MOUZA) {
      this.selectedMouzaList = [];
      this.f['mouzaIds'].setValue([]);
    }
    this.checkedAssigned[type] = [];
    this.display(type);
    this.hasUnsavedChanges = true;
  }

  // ==================== SAVE & DISCARD ====================

  updateRights(): void {
    if (!this.selectedUserId || this.isSaving) return;
    this.isSaving = true;
    this.crudService.edit(this.form.value, AppUrlConstants.PERMISSION_URL + AppUrlConstants.ADD).subscribe({
      next: (res: any) => {
        this.isSaving = false;
        this.hasUnsavedChanges = false;
        if (res?.data) {
          this.selectedWorkFlowStepsList = res.data.workFlowSteps || [];
          this.selectedDivisionList = res.data.divisions || [];
          this.selectedDistrictList = res.data.districts || [];
          this.selectedTehsilList = res.data.tehsil || [];
          this.selectedMouzaList = res.data.mouza || [];
        }
        this.updateFormIds();
        this.displayAll();
        this.clearAllChecked();
        this.openAlert('Rights updated successfully');
      },
      error: (e) => {
        this.isSaving = false;
        if (e?.status == 409 && e?.error?.errors) {
          for (const property in e.error.errors) {
            this.openAlert(e.error.errors[property]);
          }
        } else {
          this.openAlert(e?.error?.message || 'Failed to save permissions');
        }
      }
    });
  }

  discardChanges(): void {
    if (this.selectedUserId) {
      this.loadUserPermissions(this.selectedUserId);
    }
  }

  openAlert(message: string): void {
    this.dialog.open(AlertDialogComponent, {
      data: { message: message, buttonText: { cancel: 'OK' } }
    });
  }

  // ==================== HELPERS ====================

  filterBySearch(list: any[], term: string): any[] {
    if (!list || !term) return list;
    const lowerTerm = term.toLowerCase();
    return list.filter(item => item.name?.toLowerCase().includes(lowerTerm));
  }

  onFilteredListSearch(event: any, originalList: any[], property: string): any[] {
    if (event && event.target?.value) {
      const term = event.target.value.toLowerCase();
      return originalList.filter(item => item[property]?.toLowerCase().includes(term));
    }
    return originalList;
  }

  private getIds(list: any[], type: string): number[] {
    const ids: number[] = [];
    if (!list) return ids;
    if (type === this.PROCESS) {
      list.forEach(i => ids.push(i?.complaintProcessId));
    } else {
      list.forEach(i => ids.push(i?.adminStrId));
    }
    return ids;
  }

  trackByProcessId = (_: number, item: WorkflowStep) => item.complaintProcessId;
  trackByAdminStrId = (_: number, item: AdminStructureItem) => item.adminStrId;
  trackByUserId = (_: number, item: User) => item.userId;
}